// B2173 — `call(name, args)`: a typed function call, ONE implementation for
// every connection object of the package.
//
// THE FLOW. `FunctionCalls` reads `SHOW FUNCTIONS` ONE time and keeps every
// signature. A name that is not in the cache gives one more read (a new
// function); a name that is still absent is a client error, and no CALL goes
// out. A legacy function (`typed = false`) is a client error with the replace
// text of the server. Each argument is converted to the type of its parameter
// (`encodeTypedParams`), and the frame is `CALL name($1, …, $n)` with the
// B2171 typed row.
//
// A STALE SIGNATURE. A `CREATE OR REPLACE FUNCTION` on the server can change
// a signature after the read. The server then refuses the CALL with the typed
// refusal `signature` (code 2, `retry_ms` 0, not retryable). This module reads
// `SHOW FUNCTIONS` again ONE time, converts again and sends again ONE time. A
// second refusal is the answer.
//
// THE RESULT. `RETURNS <type>` gives the one value of the `result` column; a
// ROW that arrives as JSON text is parsed. `RETURNS TABLE (…)` gives the rows
// as objects.
//
// THIS FILE IMPORTS ONLY `./params.js`, on purpose. `@kinedb/embedded` carries
// a VERBATIM copy of this file (`sdk/js/embedded/src/call.js`), next to its
// copy of `params.js`. `sdk/js/embedded/src/params.test.js` reads both files
// and fails when the two copies differ.

import { encodeTypedParams } from './params.js';

/**
 * The keywords of the tokenizer (`crates/kinedb-query/src/token.rs`). A name
 * that is one of them, in any case, is not an identifier and takes brackets.
 */
const KEYWORDS = new Set((
  'AND AS ASC BEGIN BETWEEN BLOB BOOL BY CALL COLLECTION COLLECTIONS COLUMNS COMMIT COMMITS '
  + 'CREATE CROSS DATABASE DATABASES DELETE DESC DESCRIBE DOCUMENT DROP ESCAPE EXPLAIN FALSE '
  + 'FAST FILE FROM FULLTEXT FUNCTION FUNCTIONS GET ILIKE IN INDEX INDEXES INNER INSERT INT '
  + 'INTO IS JOIN KV LANGUAGE LEFT LIKE LIMIT MATCH NOT NULL ON OR ORDER OUTER PUT RIGHT '
  + 'ROLLBACK SAFE SELECT SET SHOW STATUS TABLE TABLES TEXT TRANSACTION TRANSFER TRUE TRUNCATE '
  + 'TYPE UNWATCH UPDATE USE USING VALUES VECTOR VERBOSE WATCH WHERE WITH'
).split(' '));

/**
 * The function name as SQL: bare when it is a plain identifier, else in
 * brackets (the B319 quoted identifier, the rule of `sql_ident` in
 * `kinedb-query/src/parser.rs`).
 *
 * @throws {TypeError} for a name with a `]`: no quoted form can hold it.
 */
export function quoteFunctionName(name) {
  if (/^[A-Za-z_][A-Za-z0-9_]*$/.test(name) && !KEYWORDS.has(name.toUpperCase())) return name;
  if (name.includes(']')) {
    throw new TypeError(`kinedb: the function name ${JSON.stringify(name)} has a ']', which no quoted name can hold`);
  }
  return `[${name}]`;
}

/** The CALL text for a function of `n` parameters: `CALL f($1, …, $n)`. */
export function callSql(name, n) {
  const marks = Array.from({ length: n }, (_, i) => `$${i + 1}`).join(', ');
  return `CALL ${quoteFunctionName(name)}(${marks})`;
}

/** A cell that can arrive as JSON text (a typed row) or as JSON (HTTP). */
function jsonCell(value) {
  if (typeof value !== 'string') return value;
  try { return JSON.parse(value); } catch { return value; }
}

/** The signatures of one `SHOW FUNCTIONS` result: name → `{params, returns, typed}`. */
function readSignatures(resp, makeError) {
  if (resp?.type !== 'rows' || !Array.isArray(resp.columns)) {
    throw makeError('kinedb: SHOW FUNCTIONS did not answer rows', resp);
  }
  const at = (name) => resp.columns.indexOf(name);
  const [iName, iTypes, iReturns, iTyped] = ['name', 'param_types', 'returns', 'typed'].map(at);
  if (iName < 0 || iTypes < 0 || iReturns < 0 || iTyped < 0) {
    throw makeError(
      'kinedb: SHOW FUNCTIONS has no param_types, returns or typed column: call() needs a server '
        + 'with typed function signatures (B2172)',
      resp,
    );
  }
  const out = new Map();
  for (const row of resp.data ?? []) {
    const typed = jsonCell(row[iTyped]);
    const params = jsonCell(row[iTypes]);
    out.set(row[iName], {
      params: Array.isArray(params) ? params : [],
      returns: row[iReturns],
      typed: typed === true || typed === 1 || typed === 'true',
    });
  }
  return out;
}

/** The rows of a result as objects, one key per column. */
function rowObjects(resp) {
  return (resp.data ?? []).map((row) => {
    const obj = {};
    resp.columns.forEach((c, i) => { obj[c] = row[i]; });
    return obj;
  });
}

/**
 * The answer of a CALL, by the RETURNS text of its signature.
 */
function callAnswer(name, f, resp, makeError) {
  if (resp?.type !== 'rows' || !Array.isArray(resp.columns)) {
    throw makeError(`kinedb: CALL ${name} did not answer rows`, resp);
  }
  const returns = typeof f.returns === 'string' ? f.returns : '';
  if (returns.startsWith('TABLE')) return rowObjects(resp);
  const value = resp.data?.[0]?.[0] ?? null;
  if (returns === 'ROW' || returns === 'ROW NULL') return jsonCell(value);
  return value;
}

/**
 * The signature cache and the call of one connection object.
 *
 * @param {{run: (sql: string, p: Uint8Array|null) => Promise<object>,
 *          makeError: (message: string, response?: object) => Error}} parts
 *   - `run` sends one statement with an encoded P (`null` for none) and
 *     answers the response. A server error rejects with an error whose
 *     `response` is the error response.
 *   - `makeError` builds the error class of the package.
 */
export class FunctionCalls {
  constructor({ run, makeError }) {
    this._run = run;
    this._makeError = makeError;
    /** name → signature, from the last `SHOW FUNCTIONS`; `null` before the first. */
    this._signatures = null;
    /** The `SHOW FUNCTIONS` read in flight, so parallel calls share one read. */
    this._reading = null;
  }

  /** Read `SHOW FUNCTIONS` and replace the cache. */
  async _read() {
    if (this._reading === null) {
      this._reading = (async () => {
        try {
          this._signatures = readSignatures(await this._run('SHOW FUNCTIONS', null), this._makeError);
        } finally {
          this._reading = null;
        }
      })();
    }
    await this._reading;
  }

  /**
   * The signature of `name`, and whether this call read it (`fresh`).
   * `force` forces a read; a miss in the cache gives one more read.
   */
  async _signature(name, force) {
    let fresh = false;
    if (this._signatures === null || force) {
      await this._read();
      fresh = true;
    }
    let f = this._signatures.get(name);
    if (f === undefined && !fresh) {
      await this._read();
      fresh = true;
      f = this._signatures.get(name);
    }
    if (f === undefined) throw this._makeError(`kinedb: function ${name} does not exist`);
    if (!f.typed) {
      throw this._makeError(
        `kinedb: function ${name} has no parameter types: replace it with CREATE OR REPLACE FUNCTION`,
      );
    }
    return { f, fresh };
  }

  /**
   * The signature and the P of a call. An argument that does not fit a
   * signature from an EARLIER read can fit a new one (a `CREATE OR REPLACE`
   * added a parameter), so that misfit gives one more read and one more
   * conversion. A misfit against a signature this call read is the answer.
   */
  async _prepare(name, args, force) {
    let { f, fresh } = await this._signature(name, force);
    try {
      return { f, p: encodeTypedParams(args, f.params, name) };
    } catch (err) {
      if (fresh || !(err instanceof TypeError || err instanceof RangeError)) throw err;
    }
    ({ f } = await this._signature(name, true));
    return { f, p: encodeTypedParams(args, f.params, name) };
  }

  async _send(name, { f, p }) {
    const resp = await this._run(callSql(name, f.params.length), p);
    return callAnswer(name, f, resp, this._makeError);
  }

  /**
   * Call the function `name` with `args` (an array in signature order, or an
   * object by parameter name).
   */
  async call(name, args) {
    if (typeof name !== 'string' || name === '') {
      throw new TypeError('kinedb: call(name, args) takes the function name as a string');
    }
    try {
      return await this._send(name, await this._prepare(name, args, false));
    } catch (err) {
      if (err?.response?.refusal?.kind !== 'signature') throw err;
    }
    // The signature changed on the server: one read, one send.
    return this._send(name, await this._prepare(name, args, true));
  }
}
